import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton
} from '@mui/material';
import { Plus, BrushCleaning, SearchIcon, RefreshCcw, Eye, ChevronLeft, ChevronRight, X, FileText, FileSpreadsheet, FileImage, Download } from 'lucide-react';
import DataTable from '../../common/DataTable/index';
import Search from '../../common/Search';
import CommonDialog from '../../common/CommonDialog';
import AddIssues from './AddIssues';
import { fetchIssues } from '../../../../apis/Issues';
import ExportExcel from '../../common/ExportExcel';
import { useResponsive, userScreenAccess } from '../../../../utils/MasterUtils';
import {
  FilterFieldSx,
  DropDownSX,
  CSS_PRIMARY,
  CSS_PRIMARY_HOVER,
  DropDownSXWithColor,
} from '../../../../constants/style';
import { fetchUsersCombo } from '../../../../apis/Masters/Users';
import { fetchProductListCombo } from '../../../../apis/Issues/Product';
import { fetchGlobalTypesCombo } from '../../../../apis/Masters/GlobalType';
import { MastersId } from '../../../../constants/constant';
import Tooltip from '../../../../utils/Tooltip';
import { fetchClients } from '../../../../apis/Clients';
import ChooseClient from '../../common/ChooseClient';

let _editRowHandler = null;
let _previewHandler = null;

const headers = [
  {
    label: 'Client Name',
    key: 'clientName',
    minWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Issue dATE',
    key: 'createdDate',
    minWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true,
    render: (val) => {
      if (!val) return '';

      const date = new Date(val);
      const formattedDate = `${String(date.getDate()).padStart(2, '0')}-${String(
        date.getMonth() + 1
      ).padStart(2, '0')}-${date.getFullYear()}`;

      return <span title={val}>{formattedDate}</span>;
    }
  },
  {
    label: 'Issue Type',
    key: 'issueType',
    minWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Product',
    key: 'productName',
    minWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Title',
    key: 'title',
    minWidth: 150,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Description',
    key: 'description',
    minWidth: 180,
    sortable: true,
    align: 'left',
    resizable: true,
    render: (val) => {
      const truncated = val && val.length > 50 ? `${val.substring(0, 50)}...` : val;
      return <span title={val}>{truncated}</span>;
    }
  },
  {
    label: 'Priority',
    key: 'priority',
    minWidth: 110,
    sortable: true,
    align: 'center',
    render: (val) => {
      const priorityClass =
        val === 'High'
          ? 'bg-danger-subtle text-danger'
          : val === 'Medium'
            ? 'bg-warning-subtle text-warning'
            : 'bg-info-subtle text-info';
      return <span className={`${priorityClass} px-2 py-1 fw-semibold`}>{val}</span>;
    }
  },
  {
    label: 'Status',
    key: 'status',
    minWidth: 110,
    sortable: true,
    align: 'center',
    render: (val) => {
      const statusClass =
        val === 'Closed'
          ? 'bg-success-subtle text-success'
          : val === 'Open'
            ? 'bg-info-subtle text-info'
            : val === 'In Progress'
              ? 'bg-primary-subtle text-primary'
              : val === 'Rejected'
                ? 'bg-danger-subtle text-danger'
                : 'bg-secondary-subtle text-secondary';
      return <span className={`${statusClass} px-2 py-1 fw-semibold`}>{val}</span>;
    }
  },
  {
    label: 'Assigned To',
    key: 'assignedTo',
    minWidth: 130,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Due Date',
    key: 'dueDateString',
    minWidth: 120,
    sortable: true,
    align: 'center'
  },
  {
    label: 'Preview',
    key: 'filePaths',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (val, row) => {
      const files = val && Array.isArray(val) ? val : [];
      return files.length > 0 ? (
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            _previewHandler?.(files);
          }}
          sx={{
            fontSize: '0.7rem',
            textTransform: 'none',
            borderRadius: 1.5,
            py: 0.3,
            px: 1,
            minWidth: 'auto',
            borderColor: 'divider',
            color: 'text.secondary',
            gap: 0.5,
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
          }}
        >
          <Eye size={14} />
          {files.length}
        </Button>
      ) : (
        <span className="text-muted">—</span>
      );
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:90';

const emptyFilter = {
  title: '',
  clientGUID: 0,
  productGUID: 0,
  priorityId: 0,
  assigneeGUID: 0,
  statusId: 0,
  issueTypeId: 0,
  statusList: [11001, 11002]
};

const IssuesList = () => {
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(emptyFilter);

  const [clients, setClients] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [issueTypeOptions, setIssueTypeOptions] = useState([]);
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null); // null = Add mode
  const { isMobile } = useResponsive();

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const userAccess = userScreenAccess();

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    setSelectedIssue(row);
    setOpenDialog(true);
  };

  _previewHandler = (files) => {
    setPreviewFiles(files);
    setPreviewIndex(0);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewFiles([]);
    setPreviewIndex(0);
  };

  const handlePreviewPrev = () => {
    setPreviewIndex((prev) => (prev > 0 ? prev - 1 : previewFiles.length - 1));
  };

  const handlePreviewNext = () => {
    setPreviewIndex((prev) => (prev < previewFiles.length - 1 ? prev + 1 : 0));
  };

  // Open Add dialog
  const handleAddIssue = () => {
    if (!userAccess.hasAddPermission) return;
    setSelectedIssue(null);
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedIssue(null);
  };

  const fetchIssuesListResponse = async (filters) => {
    setIsLoading(true);
    try {
      const result = filters.statusList.join(',');
      const payload = { ...filters, statusId: result };
      const response = await fetchIssues(payload);
      setIssues(response || []);
    } catch (err) {
      console.log('Error fetching issues list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const autoCompleteChange = (field, valueFeild) => (event, value) => {
    const next = {
      ...filters,
      [field]: value?.[valueFeild] || 0
    };
    setFilters(next);
  };

  useEffect(() => {
    fetchIssuesListResponse(emptyFilter);
    fetchDependencies();
  }, []);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const fetchDependencies = async () => {
    try {
      const [clientsRes, usersRes, productsRes, prioritiesRes, statusRes, issueTypeRes] =
        await Promise.all([
          fetchClients({ statusGUID: 1 }),
          fetchUsersCombo(),
          fetchProductListCombo(),
          fetchGlobalTypesCombo(MastersId.PRIORITY),
          fetchGlobalTypesCombo(MastersId.ISSUE_STATUS),
          fetchGlobalTypesCombo(MastersId.ISSUE_TYPE)
        ]);

      setClients(clientsRes || []);
      setAssigneeOptions(usersRes || []);
      setProductOptions(productsRes || []);
      setPriorityOptions(prioritiesRes || []);
      setStatusOptions(statusRes || []);
      setIssueTypeOptions(issueTypeRes || []);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  const handleFetch = () => {
    fetchIssuesListResponse(filters);
  };

  const handleResetFilters = () => {
    setFilters(emptyFilter);
    fetchIssuesListResponse(emptyFilter);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleFetch();
    }
  };

  const handleClientChange = (clientGUID) => {
    const next = { ...filters, clientGUID };
    setFilters(next);
  };

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return issues;
    return issues.filter((row) => {
      const normalizedValues = Object.entries(row).map(([, val]) => {
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [searchQuery, issues]);

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Issues List
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0px 2px 10px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)'
            },
            gap: 1,
            width: '100%',
            alignItems: 'center'
          }}
        >
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <ChooseClient
              clients={clients}
              value={filters.clientGUID}
              onKeyDown={handleKeyDown}
              onChange={handleClientChange}
            />
          </Box>

          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={productOptions}
              value={productOptions.find((p) => p.productGUID === filters.productGUID) || null}
              onChange={autoCompleteChange('productGUID', 'productGUID')}
              disablePortal={false}
              disableClearable={false}
              getOptionLabel={(option) => option?.productName || ''}
              isOptionEqualToValue={(option, value) => option?.productGUID === value?.productGUID}
              onKeyDown={handleKeyDown}
              sx={{ width: '100%', ...DropDownSXWithColor }}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Products"
                  placeholder="Select Products"
                  sx={FilterFieldSx}
                />
              )}
            />
          </Box>

          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={issueTypeOptions}
              value={issueTypeOptions.find((p) => p.globalTypeID === filters.issueTypeId) || null}
              onChange={autoCompleteChange('issueTypeId', 'globalTypeID')}
              disablePortal={false}
              disableClearable={false}
              getOptionLabel={(option) => option?.globalTypeName || ''}
              isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
              onKeyDown={handleKeyDown}
              sx={{ width: '100%', ...DropDownSXWithColor }}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Issue Type"
                  placeholder="Select Issue Type"
                  sx={FilterFieldSx}
                />
              )}
            />
          </Box>

          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={priorityOptions}
              value={priorityOptions.find((p) => p.globalTypeID === filters.priorityId) || null}
              onChange={autoCompleteChange('priorityId', 'globalTypeID')}
              disablePortal={false}
              disableClearable={false}
              getOptionLabel={(option) => option?.globalTypeName || ''}
              isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
              onKeyDown={handleKeyDown}
              sx={{ width: '100%', ...DropDownSXWithColor }}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Priority"
                  placeholder="Select Priority"
                  sx={FilterFieldSx}
                />
              )}
            />
          </Box>

          {/* <Autocomplete
            size="small"
            fullWidth
            options={statusOptions}
            value={statusOptions.find((s) => s.globalTypeID === filters.statusId) || null}
            onChange={autoCompleteChange('statusId', 'globalTypeID')}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.globalTypeName || ''}
            isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                placeholder="Select Priority"
                sx={FilterFieldSx}
              />
            )}
          /> */}

          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              fullWidth
              options={statusOptions}
              sx={{ width: '100%', ...DropDownSXWithColor }}
              onKeyDown={handleKeyDown}
              value={statusOptions.filter((m) => filters.statusList.includes(m.globalTypeID))}
              onChange={(_, selectedOptions) =>
                setFilters((prev) => ({
                  ...prev,
                  statusList: selectedOptions.map((opt) => opt.globalTypeID)
                }))
              }
              getOptionLabel={(option) => option?.globalTypeName || ''}
              isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  {option.globalTypeName}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  placeholder="Select Status"
                />
              )}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          {/* <Autocomplete
            size="small"
            fullWidth
            options={assigneeOptions}
            value={assigneeOptions.find((u) => u.userGUID === filters.assigneeGUID) || null}
            onChange={autoCompleteChange('assigneeGUID', 'userGUID')}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.userName || ''}
            isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assignee"
                placeholder="Select Assignee"
                sx={FilterFieldSx}
              />
            )}
          /> */}

          <Tooltip text="Fetch">
            <Button
              variant="outlined"
              size="small"
              onClick={handleFetch}
              sx={{
                textTransform: 'none',
                borderRadius: 1.5,
                whiteSpace: 'nowrap',
                color: CSS_PRIMARY, // text color
                borderColor: CSS_PRIMARY, // outline color

                '&:hover': {
                  borderColor: CSS_PRIMARY_HOVER,
                  color: CSS_PRIMARY_HOVER
                }
              }}
            >
              <SearchIcon size={16} />
            </Button>
          </Tooltip>

          {/* Reset Button */}
          <Tooltip text="Reset filters">
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              sx={{
                textTransform: 'none',
                borderRadius: 1.5,
                whiteSpace: 'nowrap',
                width: '32px',
                color: CSS_PRIMARY, // text color
                borderColor: CSS_PRIMARY, // outline color

                '&:hover': {
                  borderColor: CSS_PRIMARY_HOVER,
                  color: CSS_PRIMARY_HOVER
                }
              }}
            >
              <BrushCleaning size={16} />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by title, product, or status..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={issues}
            fileName={`Issues_${new Date().toISOString().slice(0, 10)}.xlsx`}
            sheetName="Issues"
            buttonLabel="Excel"
            exportKeys={headers
              .filter((h) => h.key !== '_actions' && h.key !== 'imageUrl')
              .map((h) => h.key)}
            exportHeaders={headers
              .filter((h) => h.key !== '_actions' && h.key !== 'imageUrl')
              .map((h) => h.label)}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddIssue}
            sx={{
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: userAccess.hasAddPermission ? 'inline-flex' : 'none'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* ── Data Table ────────────────────────────────────────────────── */}
      <DataTable
        headers={headers}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={
          searchQuery ? `No results for "${searchQuery}"` : 'No issues found in the system.'
        }
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      {/* ── Add / Edit Dialog ─────────────────────────────────────────── */}
      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={
          selectedIssue ? `Edit Issue — ${selectedIssue.title || 'Untitled'}` : 'Add New Issue'
        }
        maxWidth={'lg'}
        fullWidth={true}
        persistent={true}
        fullScreen={isMobile ? true : false}
      >
        <AddIssues
          clients={clients}
          products={productOptions}
          priorities={priorityOptions}
          statuses={statusOptions}
          users={assigneeOptions}
          issueData={selectedIssue}
          issueTypeOptions={issueTypeOptions}
          userAccess={userAccess}
          onRefresh={handleFetch}
          onClose={handleCloseDialog}
        />
      </CommonDialog>

      {/* ── File Preview Dialog ────────────────────────────────────────── */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: '#1a1a2e',
            position: 'relative'
          }
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handlePreviewClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            color: '#fff',
            bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' }
          }}
        >
          <X size={20} />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: 400
          }}
        >
          {/* Previous button */}
          {previewFiles.length > 1 && (
            <IconButton
              onClick={handlePreviewPrev}
              sx={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.45)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                width: 40,
                height: 40
              }}
            >
              <ChevronLeft size={24} />
            </IconButton>
          )}

          {/* File Preview */}
          {previewFiles.length > 0 && (() => {
            const currentFile = previewFiles[previewIndex];
            const fileUrl = BACKEND_URL + '/' + currentFile;
            const ext = currentFile.split('.').pop()?.toLowerCase();
            const fileName = currentFile.split('/').pop() || currentFile;

            const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
            const pdfExts = ['pdf'];

            if (imageExts.includes(ext)) {
              return (
                <Box
                  component="img"
                  src={fileUrl}
                  alt={`Preview ${previewIndex + 1}`}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '75vh',
                    objectFit: 'contain',
                    display: 'block',
                    mx: 'auto'
                  }}
                />
              );
            }

            if (pdfExts.includes(ext)) {
              return (
                <Box
                  component="iframe"
                  src={fileUrl}
                  title={`PDF Preview ${previewIndex + 1}`}
                  sx={{
                    width: '100%',
                    height: '75vh',
                    border: 'none'
                  }}
                />
              );
            }

            // Other file types — show icon + download
            const docExts = ['doc', 'docx'];
            const sheetExts = ['xls', 'xlsx', 'csv'];
            let FileIcon = FileText;
            let iconColor = '#90caf9';
            let iconBg = 'rgba(144,202,249,0.12)';
            let fileLabel = 'Document';

            if (docExts.includes(ext)) {
              FileIcon = FileText;
              iconColor = '#64b5f6';
              iconBg = 'rgba(100,181,246,0.12)';
              fileLabel = 'Word Document';
            } else if (sheetExts.includes(ext)) {
              FileIcon = FileSpreadsheet;
              iconColor = '#81c784';
              iconBg = 'rgba(129,199,132,0.12)';
              fileLabel = 'Spreadsheet';
            } else if (ext === 'txt') {
              fileLabel = 'Text File';
            }

            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  py: 6,
                  px: 3
                }}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: iconBg
                  }}
                >
                  <FileIcon size={48} color={iconColor} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{ color: CSS_PRIMARY, fontWeight: 500, textAlign: 'center', wordBreak: 'break-all', maxWidth: 400 }}
                >
                  {fileName}
                </Typography>
                <Typography variant="caption" sx={{ color: CSS_PRIMARY }}>
                  {fileLabel} (.{ext})
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download size={16} />}
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 1,
                    color: CSS_PRIMARY,
                    borderColor: CSS_PRIMARY,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      borderColor: CSS_PRIMARY,
                    }
                  }}
                >
                  Open / Download
                </Button>
              </Box>
            );
          })()}

          {/* Next button */}
          {previewFiles.length > 1 && (
            <IconButton
              onClick={handlePreviewNext}
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.45)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                width: 40,
                height: 40
              }}
            >
              <ChevronRight size={24} />
            </IconButton>
          )}
        </DialogContent>

        {/* Counter */}
        {previewFiles.length > 1 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 1,
              color: '#fff',
              fontSize: '0.85rem',
              bgcolor: 'rgba(0,0,0,0.3)'
            }}
          >
            {previewIndex + 1} / {previewFiles.length}
          </Box>
        )}
      </Dialog>
    </div>
  );
};

export default IssuesList;
